<template>
  <div class="tools">
    <el-tabs v-model="activeCategory" @tab-change="handleCategoryChange">
      <el-tab-pane 
        v-for="category in activeCategories" 
        :key="category.id"
        :label="getCategoryLabel(category)" 
        :name="category.name" 
      />
    </el-tabs>

    <div class="tools-list">
      <el-card v-for="item in items" :key="item.id" class="tool-card" @click="goToDetail(item.id)">
        <div class="tool-card-content">
          <img v-if="item.cover_image_url" :src="item.cover_image_url" class="tool-image" />
          <div class="tool-info">
            <div class="tool-title-row">
              <h3>{{ item.title }}</h3>
              <el-tag v-if="item.is_pinned" type="warning" size="small" effect="dark">置顶</el-tag>
            </div>
            <p class="tool-meta">
              <span>{{ item.category?.name || '未分类' }}</span>
              <span>{{ formatDate(item.created_at) }}</span>
              <span>浏览 {{ item.view_count }}</span>
            </p>
            <p class="tool-excerpt">{{ getExcerpt(item.content) }}</p>
          </div>
        </div>
      </el-card>

      <div v-if="items.length === 0" class="empty-container">
        <el-empty description="暂无数据" />
      </div>

      <el-pagination
        v-model:current-page="currentPage"
        :page-sizes="[20, 50, 100]"
        :page-size="pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        class="pagination"
      />
    </div>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'ToolsPage',
  data() {
    return {
      activeCategory: null,
      items: [],
      categories: [],
      currentPage: 1,
      pageSize: 20,
      total: 0,
      categoryMap: {
        latest: '最新的工具',
        development: 'AI发展状况',
        opensource: '优秀开源项目',
        thirdparty: '好用的第三方工具'
      }
    }
  },
  computed: {
    activeCategories() {
      // 只返回启用的分类
      return this.categories.filter(cat => cat.is_active !== false)
    }
  },
  mounted() {
    this.loadCategories()
  },
  methods: {
    async loadCategories() {
      try {
        const response = await api.get('/tools/categories/')
        const categoriesList = Array.isArray(response.data) 
          ? response.data 
          : (response.data.results || [])
        this.categories = categoriesList
        
        // 如果有启用的分类，设置默认选中第一个
        if (this.activeCategories.length > 0 && !this.activeCategory) {
          this.activeCategory = this.activeCategories[0].name
          this.loadItems()
        }
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    },
    async loadItems() {
      if (!this.activeCategory) return
      
      try {
        const category = this.categories.find(c => c.name === this.activeCategory)
        
        if (!category) {
          console.warn(`未找到分类: ${this.activeCategory}`)
          this.items = []
          this.total = 0
          return
        }

        const response = await api.get('/tools/items/', {
          params: {
            category: category.id,
            page: this.currentPage,
            page_size: this.pageSize
          }
        })
        
        // 处理分页和非分页响应格式
        if (Array.isArray(response.data)) {
          this.items = response.data
          this.total = response.data.length
        } else {
          this.items = response.data.results || []
          this.total = response.data.count || 0
        }
      } catch (error) {
        console.error('加载工具失败:', error)
        this.items = []
        this.total = 0
        if (this.$message) {
          this.$message.error('加载工具失败，请稍后重试')
        }
      }
    },
    getCategoryLabel(category) {
      // 优先使用 display_name，如果没有则使用 categoryMap，最后使用 name
      if (category.display_name) {
        return category.display_name
      }
      return this.categoryMap[category.name] || category.name
    },
    handleCategoryChange() {
      // 如果当前选中的分类被禁用，切换到第一个启用的分类
      if (this.activeCategory && !this.activeCategories.find(cat => cat.name === this.activeCategory)) {
        if (this.activeCategories.length > 0) {
          this.activeCategory = this.activeCategories[0].name
        } else {
          this.activeCategory = null
        }
      }
      this.currentPage = 1
      this.loadItems()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadItems()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadItems()
    },
    goToDetail(id) {
      this.$router.push(`/tools/${id}`)
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    getExcerpt(content) {
      if (!content) return ''
      const text = content.replace(/<[^>]*>/g, '').substring(0, 150)
      return text.length < content.length ? text + '...' : text
    }
  }
}
</script>

<style scoped>
.tools {
  width: 100%;
}

.tools-list {
  margin-top: 20px;
}

.tool-card {
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.tool-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.tool-card-content {
  display: flex;
  gap: 20px;
}

.tool-image {
  width: 200px;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
}

.tool-info {
  flex: 1;
}

.tool-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.tool-info h3 {
  margin: 0;
  color: #e0e0e0;
  flex: 1;
}

.tool-info h3 {
  margin-bottom: 10px;
  color: #e0e0e0;
}

.tool-meta {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin-bottom: 10px;
}

.tool-meta span {
  margin-right: 15px;
}

.tool-excerpt {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
}

.pagination {
  margin-top: 20px;
  text-align: center;
}

.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}
</style>

